import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { type JSX, useEffect, useState } from 'react';
import { dsClass } from '../../../../common/constants/dsClasses';
import { fetchBillingAccount, fetchBillingAccounts } from '../../../../common/fetch';
import { selectMsg, t } from '../../../../common/i18n';
import { CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE } from '../../../../common/utils/billingAccountUtils';
import { SourceSystem } from '../../../../generated/api/sourceSystem';
import { getBillingAccountListItems } from '../../../AddOrSelectBillingAccountsV2/AddOrSelectBillingAccounts';
import { BillingAccountInfo } from '../../../AddOrSelectBillingAccountsV2/BillingAccountInfo';
import { BillingAccountForm } from '../../../BillingAccountForm/BillingAccountForm';
import { Loading } from '../../../Loading';
import { ConfirmModal } from './ConfirmModal';
import type { BillingAccount } from '../../../../generated/api/billingAccount';
import type { BillingAccountSearchResponse } from '../../../../generated/api/billingAccountSearchResponse';

interface SelectBillingAccountProps {
  onChange: (ba: BillingAccount) => void;
  onCreatingNewBillingAccount: () => void;
  onFinishedCreatingNewBillingAccount: () => void;
  billingAccountId?: string;
  infoText?: JSX.Element;
  mdmId: string;
  error?: string;
  disabled?: boolean;
}

export const SelectBillingAccount = ({
  onChange,
  onCreatingNewBillingAccount,
  onFinishedCreatingNewBillingAccount,
  billingAccountId,
  infoText,
  mdmId,
  error,
  disabled = false,
}: SelectBillingAccountProps) => {
  const [searchText, setSearchText] = useState('');
  const [selectedBillingAccount, setSelectedBillingAccount] = useState<BillingAccount | undefined>();
  const [isFetchingSelectedBillingAccount, setIsFetchingSelectedBillingAccount] = useState(false);
  const [isCreatingNewBillingAccount, setIsCreatingNewBillingAccount] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<string | undefined>(undefined);
  const [newBillingAccountIsDirty, setNewBillingAccountIsDirty] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [comboboxKey, setComboboxKey] = useState(0);
  const [billingAccountSearchResponses, setBillingAccountSearchResponses] = useState<
    BillingAccountSearchResponse[] | undefined
  >();

  const formattedBillingAccounts = getBillingAccountListItems(!disabled, billingAccountSearchResponses, searchText).map(
    item => ({ ...item, id: item.value })
  );

  const resetSelectionState = () => {
    setSelectedBillingAccount(undefined);
    setIsFetchingSelectedBillingAccount(false);
  };

  const fetchAndSelectBillingAccount = async (internalId: string, listOverride?: BillingAccountSearchResponse[]) => {
    const searchList = listOverride ?? billingAccountSearchResponses;
    const baDisplayId = searchList?.find(r => r.result.billingAccountId === internalId)?.result
      ?.billingAccountDisplayId;
    if (!baDisplayId) {
      resetSelectionState();
      return;
    }
    setIsFetchingSelectedBillingAccount(true);
    setSelectedBillingAccount(undefined);
    try {
      const res = await fetchBillingAccount(baDisplayId, mdmId);
      const ba = res.billingAccounts?.[0];
      setSelectedBillingAccount(ba);
      if (ba) {
        onChange(ba);
      }
    } catch {
      resetSelectionState();
    } finally {
      setIsFetchingSelectedBillingAccount(false);
    }
  };

  const refreshBillingAccounts = async (newlyCreatedInternalId?: string) => {
    try {
      if (newlyCreatedInternalId) {
        // When a new billing account was just created, it may not yet be indexed in Elasticsearch.
        // Fetch directly from Salesforce (omit useSearchService) to ensure we see the new BA immediately.
        const salesforceRes = await fetchBillingAccounts({ sourceSystem: SourceSystem.SFDC }, { mdmId: mdmId });
        const bAccounts = salesforceRes.billingAccounts ?? [];
        // Map raw Salesforce billing accounts into elastic header shape
        const mapped = bAccounts.map(ba => ({ result: { ...ba } }));
        setBillingAccountSearchResponses(mapped);
        const exists = mapped.some(r => r.result.billingAccountId === newlyCreatedInternalId);
        if (exists) {
          void fetchAndSelectBillingAccount(newlyCreatedInternalId, mapped);
        }
        return;
      }
      // Default path: use Elasticsearch for performance
      const res = await fetchBillingAccounts(
        { useSearchService: true, sourceSystem: SourceSystem.SFDC },
        { mdmId: mdmId }
      );
      const results = res.searchResults;
      setBillingAccountSearchResponses(results);
    } catch {
      setBillingAccountSearchResponses(undefined);
    }
  };

  const handleBillingAccountSelection = (value: string) => {
    // If user is creating a new billing account and tries to select a different option, show confirmation modal
    if (isCreatingNewBillingAccount && value !== CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE && newBillingAccountIsDirty) {
      setPendingSelection(value);
      setShowConfirmModal(true);
      return;
    }

    if (value === CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE) {
      setIsCreatingNewBillingAccount(true);
      onCreatingNewBillingAccount();
      resetSelectionState();
      return;
    }
    setIsCreatingNewBillingAccount(false);
    void fetchAndSelectBillingAccount(value);
  };

  const handleConfirmSelectionChange = () => {
    setShowConfirmModal(false);
    setIsCreatingNewBillingAccount(false);
    onFinishedCreatingNewBillingAccount();
    if (pendingSelection) {
      void fetchAndSelectBillingAccount(pendingSelection);
      setPendingSelection(undefined);
    }
  };

  const handleCancelSelectionChange = () => {
    setShowConfirmModal(false);
    setPendingSelection(undefined);
    setSearchText('');
    // Force combobox to re-render with the correct selected value
    setComboboxKey(prev => prev + 1);
  };

  const onValueSelect = (_value: unknown, item: unknown) => {
    const el = item as HTMLLIElement | undefined;
    const datasetValue = el?.dataset?.value;
    if (datasetValue) {
      handleBillingAccountSelection(datasetValue);
    }
    setSearchText('');
  };

  useEffect(() => {
    void refreshBillingAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mdmId]);

  useEffect(() => {
    if (!billingAccountId || isCreatingNewBillingAccount) {
      return;
    }
    if (!billingAccountSearchResponses) {
      return;
    }
    if (selectedBillingAccount?.billingAccountId === billingAccountId) {
      return;
    }
    if (isFetchingSelectedBillingAccount) {
      return;
    }
    const exists = billingAccountSearchResponses.some(r => r.result.billingAccountId === billingAccountId);
    if (exists) {
      void fetchAndSelectBillingAccount(billingAccountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    billingAccountId,
    billingAccountSearchResponses,
    selectedBillingAccount,
    isCreatingNewBillingAccount,
    isFetchingSelectedBillingAccount,
    mdmId,
  ]);

  return (
    <>
      <CL.Combobox
        key={comboboxKey}
        i18n_combobox_errorMessage={error}
        label={t.PUM6('Choose billing account')}
        i18n_combobox_infoText={infoText}
        onValueSelect={onValueSelect}
        items={formattedBillingAccounts}
        selectedValue={
          isCreatingNewBillingAccount
            ? CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE
            : selectedBillingAccount?.billingAccountId || (isFetchingSelectedBillingAccount && billingAccountId) || ''
        }
        onValueChange={value => {
          if (value) {
            setSearchText(value);
          }
        }}
        selectedItemFormat="label"
        i18n_combobox_buttonAriaLabel={t.HVS2('Selected billing account')}
        i18n_combobox_placeholderText={t.QRYV(selectMsg)}
        disabled={disabled}
      />

      {isCreatingNewBillingAccount && !disabled && (
        <BillingAccountForm
          embeddedMode={{ mdmId: mdmId }}
          onCreated={baId => {
            setIsCreatingNewBillingAccount(false);
            // Refresh list and select new billing account
            void refreshBillingAccounts(baId);
            onFinishedCreatingNewBillingAccount();
          }}
          onCancel={() => {
            setIsCreatingNewBillingAccount(false);
            onFinishedCreatingNewBillingAccount();
          }}
          onDirtyState={(isDirty: boolean) => {
            setNewBillingAccountIsDirty(isDirty);
          }}
        />
      )}

      {!isCreatingNewBillingAccount && (isFetchingSelectedBillingAccount || selectedBillingAccount) ? (
        <div className={classNames(dsClass.MARGIN_VERTICAL_4)}>
          {selectedBillingAccount ? (
            <BillingAccountInfo billingAccount={selectedBillingAccount} />
          ) : (
            <Loading large={true} bottomPadding={4} />
          )}
        </div>
      ) : null}

      {showConfirmModal && (
        <ConfirmModal onStay={handleCancelSelectionChange} onContinue={handleConfirmSelectionChange} />
      )}
    </>
  );
};
